/**
 * Product Card Actions — Wishlist toggle + Quick Add to Cart
 * Works on collection pages, homepage, and anywhere product-card snippets are rendered.
 * Delegates events from the document level so dynamically injected cards also work.
 */
(function () {
  'use strict';

  /* ── localStorage helpers ── */
  function savedHandle(saved) {
    return typeof saved === 'string' ? saved : saved && typeof saved.handle === 'string' ? saved.handle : '';
  }

  function normalizeOptions(options) {
    var source = options && typeof options === 'object' ? options : {};
    var normalized = {};
    var colour = source.colour || source.color;
    if (typeof colour === 'string' && colour.trim()) normalized.colour = colour.trim();
    ['size', 'orientation', 'option1', 'option2', 'option3'].forEach(function (key) {
      if (typeof source[key] === 'string' && source[key].trim()) normalized[key] = source[key].trim();
    });
    return normalized;
  }

  function normalizeSaved(saved) {
    var handle = savedHandle(saved);
    if (!handle) return null;
    if (typeof saved === 'string') return { handle: handle, variantId: null, options: {}, partial: true };
    var variantId = parseInt(saved.variantId, 10) || null;
    var options = normalizeOptions(saved.options);
    return {
      handle: handle,
      variantId: variantId,
      options: options,
      partial: saved.partial === true || (!variantId && Object.keys(options).length === 0)
    };
  }

  function recordScore(saved) {
    return (saved.variantId ? 100 : 0) + Object.keys(saved.options || {}).length * 10 + (saved.partial ? 0 : 1);
  }

  function wishlistRecordKey(saved) {
    var handle = savedHandle(saved);
    if (!handle) return '';
    var variantId = saved && typeof saved === 'object' ? parseInt(saved.variantId, 10) || null : null;
    var isPartial = typeof saved === 'string' || !variantId || (saved && saved.partial === true);
    return handle + (isPartial ? '::partial' : '::variant:' + variantId);
  }

  function normalizeWishlist(items) {
    var normalized = [];
    var positions = Object.create(null);
    (Array.isArray(items) ? items : []).forEach(function (raw) {
      var saved = normalizeSaved(raw);
      if (!saved) return;
      var key = wishlistRecordKey(saved);
      var existingPosition = positions[key];
      if (existingPosition === undefined) {
        positions[key] = normalized.length;
        normalized.push(saved);
      } else if (recordScore(saved) >= recordScore(normalized[existingPosition])) {
        normalized[existingPosition] = saved;
      }
    });
    var handlesWithExactVariants = Object.create(null);
    normalized.forEach(function (saved) {
      if (saved.variantId && !saved.partial) handlesWithExactVariants[saved.handle] = true;
    });
    return normalized.filter(function (saved) {
      return !(saved.partial && handlesWithExactVariants[saved.handle]);
    });
  }

  function serializableSaved(saved) {
    return {
      handle: saved.handle,
      variantId: parseInt(saved.variantId, 10) || null,
      options: normalizeOptions(saved.options),
      partial: saved.partial === true
    };
  }

  function getWishlist() {
    try {
      var stored = JSON.parse(localStorage.getItem('aykah_wishlist') || '[]');
      var normalized = normalizeWishlist(stored);
      var serialized = normalized.map(serializableSaved);
      if (JSON.stringify(stored) !== JSON.stringify(serialized)) {
        localStorage.setItem('aykah_wishlist', JSON.stringify(serialized));
      }
      return normalized;
    } catch (e) {
      return [];
    }
  }

  function setWishlist(items) {
    localStorage.setItem('aykah_wishlist', JSON.stringify(normalizeWishlist(items).map(serializableSaved)));
    /* Notify header badge */
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
  }

  /* ── Phosphor heart: swap between outline (Regular) and solid (Fill) ── */
  window.AykahSetHeart = window.AykahSetHeart || function (svg, active) {
    if (!svg) return;
    svg.setAttribute('viewBox', '0 0 256 256');
    svg.setAttribute('fill', 'currentColor');
    svg.innerHTML = active
      ? '<path d="M240,102c0,70-103.79,126.66-108.21,129a8,8,0,0,1-7.58,0C119.79,228.66,16,172,16,102A62.07,62.07,0,0,1,78,40c20.65,0,38.73,8.88,50,23.89C139.27,48.88,157.35,40,178,40A62.07,62.07,0,0,1,240,102Z"/>'
      : '<path d="M178,40c-20.65,0-38.73,8.88-50,23.89C116.73,48.88,98.65,40,78,40a62.07,62.07,0,0,0-62,62c0,70,103.79,126.66,108.21,129a8,8,0,0,0,7.58,0C136.21,228.66,240,172,240,102A62.07,62.07,0,0,0,178,40ZM128,214.8C109.74,204.16,32,155.69,32,102A46.06,46.06,0,0,1,78,56c19.45,0,35.78,10.36,42.6,27a8,8,0,0,0,14.8,0c6.82-16.67,23.15-27,42.6-27a46.06,46.06,0,0,1,46,46C224,155.61,146.24,204.15,128,214.8Z"/>';
  };

  /* ── Mark wishlisted cards on page load ── */
  function markWishlistedCards() {
    var wishlist = getWishlist();
    var buttons = document.querySelectorAll('.product-card-wishlist[data-product-handle]');
    buttons.forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      var svg = btn.querySelector('svg');
      if (wishlist.some(function (saved) { return savedHandle(saved) === handle; })) {
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Remove from wishlist');
        btn.classList.add('text-brand-error', '!opacity-100');
        btn.classList.remove('text-brand-text-muted');
        if (svg) window.AykahSetHeart(svg, true);
      } else {
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Add to wishlist');
        btn.classList.remove('text-brand-error', '!opacity-100');
        btn.classList.add('text-brand-text-muted');
        if (svg) window.AykahSetHeart(svg, false);
      }
    });
  }

  /* ── Wishlist toggle (capture phase — fires BEFORE <a> link navigation) ── */
  document.addEventListener('click', function (e) {
    var wishlistBtn = e.target.closest('.product-card-wishlist[data-product-handle]');
    if (!wishlistBtn) return;

    e.preventDefault();
    e.stopPropagation();

    var handle = wishlistBtn.getAttribute('data-product-handle');
    if (!handle) return;

    var wishlist = getWishlist();
    var idx = wishlist.findIndex(function (saved) { return savedHandle(saved) === handle; });
    var svg = wishlistBtn.querySelector('svg');

    if (idx > -1) {
      /* Remove from wishlist */
      wishlist = wishlist.filter(function (saved) { return savedHandle(saved) !== handle; });
      wishlistBtn.setAttribute('aria-pressed', 'false');
      wishlistBtn.setAttribute('aria-label', 'Add to wishlist');
      wishlistBtn.classList.remove('text-brand-error', '!opacity-100');
      wishlistBtn.classList.add('text-brand-text-muted');
      if (svg) window.AykahSetHeart(svg, false);
    } else {
      /* Add to wishlist */
      wishlist.push({
        handle: handle,
        variantId: parseInt(wishlistBtn.getAttribute('data-variant-id'), 10) || null,
        options: {},
        partial: true
      });
      wishlistBtn.setAttribute('aria-pressed', 'true');
      wishlistBtn.setAttribute('aria-label', 'Remove from wishlist');
      wishlistBtn.classList.add('text-brand-error', '!opacity-100');
      wishlistBtn.classList.remove('text-brand-text-muted');
      if (svg) window.AykahSetHeart(svg, true);

      /* Quick pulse animation */
      wishlistBtn.style.transform = 'scale(1.2)';
      setTimeout(function () {
        wishlistBtn.style.transform = '';
      }, 200);
    }

    setWishlist(wishlist);
  }, true);  /* true = capture phase */

  /* ── Quick Add to Cart (capture phase — fires BEFORE <a> navigation) ── */
  /* Seed the drawer's rules from every card on this page, so the rules are
     known before anything is added rather than only after. Cheap: it reads
     attributes already in the HTML and touches nothing else. */
  (function seedQtyRules() {
    var cards = document.querySelectorAll('.product-card-atc[data-product-id]');
    if (!cards.length) return;
    window.AykahQtyRules = window.AykahQtyRules || {};
    Array.prototype.forEach.call(cards, function (btn) {
      var id = btn.getAttribute('data-product-id');
      var mn = parseInt(btn.getAttribute('data-qty-min'), 10);
      var st = parseInt(btn.getAttribute('data-qty-step'), 10);
      var mx = parseInt(btn.getAttribute('data-qty-max'), 10);
      if (isNaN(mn) || mn < 1) mn = 1;
      if (isNaN(st) || st < 1) st = 1;
      if (isNaN(mx) || mx < 1) mx = 0;
      if (!id || (mn <= 1 && st <= 1 && mx === 0)) return;
      window.AykahQtyRules[String(id)] = { min: mn, step: st, max: mx };
    });
  })();

  document.addEventListener('click', function (e) {
    var atcBtn = e.target.closest('.product-card-atc[data-variant-id]');
    if (!atcBtn) return;

    e.preventDefault();
    e.stopPropagation();

    var variantId = parseInt(atcBtn.getAttribute('data-variant-id'), 10);
    if (!variantId) return;

    /* Quick add used to post a hard-coded quantity of 1. A chair that ships two
       to a box then landed in the cart as a single, which is the exact order the
       minimum exists to prevent. The card carries the product's minimum, so add
       that instead. Products without a rule still add 1. */
    var cardMin = parseInt(atcBtn.getAttribute('data-qty-min'), 10);
    if (isNaN(cardMin) || cardMin < 1) cardMin = 1;
    var cardStep = parseInt(atcBtn.getAttribute('data-qty-step'), 10);
    if (isNaN(cardStep) || cardStep < 1) cardStep = 1;
    var cardMax = parseInt(atcBtn.getAttribute('data-qty-max'), 10);
    if (isNaN(cardMax) || cardMax < 1) cardMax = 0;
    var cardProductId = atcBtn.getAttribute('data-product-id');

    /* The cart drawer renders from /cart.js, which carries no metafields, so it
       reads its rules from window.AykahQtyRules. header.liquid fills that in for
       whatever was already in the cart at page load, which is nothing when a
       shopper quick-adds from a collection into an empty cart. The drawer then
       fell back to stepping by 1 and a pair could be walked down to a single.
       Register this product before the drawer re-renders. */
    if (cardProductId && (cardMin > 1 || cardStep > 1 || cardMax > 0)) {
      window.AykahQtyRules = window.AykahQtyRules || {};
      window.AykahQtyRules[String(cardProductId)] = { min: cardMin, step: cardStep, max: cardMax };
    }

    /* Prevent double-clicks */
    if (atcBtn.disabled) return;
    atcBtn.disabled = true;

    var originalText = atcBtn.innerHTML;
    atcBtn.innerHTML = '<svg class="w-3.5 h-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M232,128a104,104,0,0,1-208,0c0-41,23.81-78.36,60.66-95.27a8,8,0,0,1,6.68,14.54C60.15,61.59,40,93.27,40,128a88,88,0,0,0,176,0c0-34.73-20.15-66.41-51.34-80.73a8,8,0,0,1,6.68-14.54C208.19,49.64,232,87,232,128Z"/></svg> Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: cardMin }] }),
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (data) {
            throw new Error(data.description || data.message || 'Failed');
          });
        }
        return res.json();
      })
      .then(function () {
        atcBtn.classList.remove('bg-foreground');
        atcBtn.classList.add('bg-green-700');
        atcBtn.innerHTML = '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg> Added!';
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:open'));
        setTimeout(function () {
          atcBtn.classList.add('bg-foreground');
          atcBtn.classList.remove('bg-green-700');
          atcBtn.innerHTML = originalText;
          atcBtn.disabled = false;
        }, 2000);
      })
      .catch(function () {
        atcBtn.classList.remove('bg-foreground');
        atcBtn.classList.add('bg-red-600');
        atcBtn.textContent = 'Error';
        setTimeout(function () {
          atcBtn.classList.add('bg-foreground');
          atcBtn.classList.remove('bg-red-600');
          atcBtn.innerHTML = originalText;
          atcBtn.disabled = false;
        }, 2000);
      });
  }, true);  /* true = capture phase */

  /* ── Option pill clicks — navigate to product page with option pre-selected ── */
  document.addEventListener('click', function (e) {
    var pill = e.target.closest('[data-option-url]');
    if (!pill) return;

    e.preventDefault();
    e.stopPropagation();

    window.location.href = pill.getAttribute('data-option-url');
  }, true);  /* true = capture phase */

  /* ── Wishlist badge in header ── */
  function updateHeaderWishlistBadge() {
    var count = getWishlist().length;
    var wishlistBtn = document.getElementById('wishlist-drawer-toggle');
    if (!wishlistBtn) return;

    var badgeRoot = wishlistBtn.querySelector('[data-icon-wrap]') || wishlistBtn;
    var badge = badgeRoot.querySelector('[data-wishlist-badge]');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.setAttribute('data-wishlist-badge', '');
        badge.className = 'absolute -top-0.5 -right-0.5 w-4 h-4 bg-brand-accent-btn text-white text-[10px] font-semibold rounded-full flex items-center justify-center leading-none pointer-events-none';
        badgeRoot.appendChild(badge);
      }
      badge.textContent = count;
    } else if (badge) {
      badge.remove();
    }
  }

  /* Listen for wishlist updates */
  document.addEventListener('wishlist:updated', function () {
    markWishlistedCards();
    updateHeaderWishlistBadge();
  });

  /* ── Init on DOM ready ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      markWishlistedCards();
      updateHeaderWishlistBadge();
    });
  } else {
    markWishlistedCards();
    updateHeaderWishlistBadge();
  }
})();
